import { useState, useEffect } from 'react';
import { apiGet, apiPost, apiPut } from '../services/api';
import { formatDuration } from '../utils/time';
import { X, Check } from 'lucide-react';

interface TimeEntry {
  id: string;
  project_name: string | null;
  project_color: string | null;
  description: string | null;
  start_time: string;
  duration: number | null;
  is_billable: boolean;
}

interface Timesheet {
  id: string;
  title: string;
  start_date: string;
  end_date: string;
  notes: string | null;
}

interface TimesheetDetail {
  id: string;
  title: string;
  start_date: string;
  end_date: string;
  notes: string | null;
  entries: TimeEntry[];
}

interface Props {
  timesheet?: Timesheet | null;
  onClose: () => void;
  onSaved: () => void;
}

export default function TimesheetModal({ timesheet, onClose, onSaved }: Props) {
  const [title, setTitle] = useState(timesheet?.title || '');
  const [startDate, setStartDate] = useState(timesheet?.start_date || '');
  const [endDate, setEndDate] = useState(timesheet?.end_date || '');
  const [notes, setNotes] = useState(timesheet?.notes || '');
  const [availableEntries, setAvailableEntries] = useState<TimeEntry[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [loadingEntries, setLoadingEntries] = useState(false);

  useEffect(() => {
    if (timesheet) {
      loadExistingEntries();
    }
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      fetchAvailableEntries();
    }
  }, [startDate, endDate]);

  const loadExistingEntries = async () => {
    if (!timesheet) return;
    try {
      const data = await apiGet<TimesheetDetail>(`/api/timesheets/${timesheet.id}`);
      const ids = new Set(data.entries.map((e) => e.id));
      setSelectedIds(ids);
    } catch (err) {
      console.error('Failed to load existing entries:', err);
    }
  };

  const fetchAvailableEntries = async () => {
    setLoadingEntries(true);
    try {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);

      const params = new URLSearchParams();
      params.set('page_size', '100');
      params.set('start_date', start.toISOString());
      params.set('end_date', end.toISOString());

      const data = await apiGet<{ items: TimeEntry[] }>(`/api/time-entries?${params}`);
      // Only show completed entries
      setAvailableEntries(data.items.filter((e) => e.duration !== null));
    } catch (err) {
      console.error('Failed to fetch entries:', err);
    } finally {
      setLoadingEntries(false);
    }
  };

  const toggleEntry = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedIds(next);
  };

  const totalSelectedDuration = availableEntries
    .filter((e) => selectedIds.has(e.id))
    .reduce((sum, e) => sum + (e.duration || 0), 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    if (!startDate || !endDate) {
      setError('Date range is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title,
        start_date: startDate,
        end_date: endDate,
        notes: notes || null,
        time_entry_ids: Array.from(selectedIds),
      };

      if (timesheet) {
        await apiPut(`/api/timesheets/${timesheet.id}`, payload);
      } else {
        await apiPost('/api/timesheets', payload);
      }
      onSaved();
    } catch (err: any) {
      setError(err.message || 'Failed to save timesheet');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-dark-900 rounded-xl border border-dark-800 p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">
            {timesheet ? 'Edit Timesheet' : 'New Timesheet'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Week 1 - February 2026"
              className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Optional notes..."
              className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Time entries selection */}
          {startDate && endDate && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-400">
                  Time Entries ({selectedIds.size} selected)
                </label>
                <span className="text-sm text-primary-400">
                  Total: {formatDuration(totalSelectedDuration)}
                </span>
              </div>
              <div className="bg-dark-800 border border-dark-700 rounded-lg max-h-60 overflow-y-auto">
                {loadingEntries ? (
                  <div className="p-4 text-center text-gray-500">Loading entries...</div>
                ) : availableEntries.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">No completed entries in this date range</div>
                ) : (
                  availableEntries.map((entry) => (
                    <label
                      key={entry.id}
                      className="flex items-center gap-3 px-3 py-2 hover:bg-dark-700 cursor-pointer border-b border-dark-700/50 last:border-0"
                    >
                      <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                        selectedIds.has(entry.id)
                          ? 'bg-primary-500 border-primary-500'
                          : 'border-dark-600'
                      }`}>
                        {selectedIds.has(entry.id) && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(entry.id)}
                        onChange={() => toggleEntry(entry.id)}
                        className="sr-only"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          {entry.project_name && (
                            <span className="text-xs px-1.5 py-0.5 rounded bg-dark-600 text-gray-300">
                              {entry.project_name}
                            </span>
                          )}
                          <span className="text-sm text-white truncate">
                            {entry.description || 'No description'}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500">
                          {new Date(entry.start_time).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short' })}
                        </span>
                      </div>
                      <span className="text-sm font-mono text-gray-400">
                        {entry.duration ? formatDuration(entry.duration) : '--:--:--'}
                      </span>
                    </label>
                  ))
                )}
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-dark-800 hover:bg-dark-700 text-gray-300 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white rounded-lg transition-colors"
            >
              {saving ? 'Saving...' : timesheet ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
